import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface MarketingArticle {
  id: string;
  slug: string;
  category: string;
  title_fr: string;
  title_en: string | null;
  title_es?: string | null;
  title_ar?: string | null;
  excerpt_fr: string;
  excerpt_en: string | null;
  excerpt_es?: string | null;
  excerpt_ar?: string | null;
  body_fr: string;
  body_en: string | null;
  body_es?: string | null;
  body_ar?: string | null;
  hero_image_url: string | null;
  hero_image_credit: string | null;
  author_name: string;
  author_avatar_url: string | null;
  reading_minutes: number;
  published_at: string;
  updated_at: string;
  is_featured: boolean;
  is_published: boolean;
  meta_title_fr: string | null;
  meta_title_en: string | null;
  meta_title_es?: string | null;
  meta_title_ar?: string | null;
  meta_description_fr: string | null;
  meta_description_en: string | null;
  meta_description_es?: string | null;
  meta_description_ar?: string | null;
  canonical_url: string | null;
  related_slugs: string[];
  primary_keyword: string | null;
  sort_order: number;
}

const SUMMARY_COLS =
  "id, slug, category, title_fr, title_en, title_es, title_ar, excerpt_fr, excerpt_en, excerpt_es, excerpt_ar, hero_image_url, reading_minutes, published_at, is_featured, sort_order";

type SummaryFields =
  | "id"
  | "slug"
  | "category"
  | "title_fr"
  | "title_en"
  | "title_es"
  | "title_ar"
  | "excerpt_fr"
  | "excerpt_en"
  | "excerpt_es"
  | "excerpt_ar"
  | "hero_image_url"
  | "reading_minutes"
  | "published_at"
  | "is_featured"
  | "sort_order";

export type MarketingArticleSummary = Pick<MarketingArticle, SummaryFields>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const tenantDb = supabase as any;

export function useMarketingArticles(category?: string) {
  return useQuery<MarketingArticleSummary[]>({
    queryKey: ["marketing-articles", category ?? "all"],
    queryFn: async () => {
      let q = tenantDb
        .from("marketing_articles")
        .select(SUMMARY_COLS)
        .eq("is_published", true)
        .order("is_featured", { ascending: false })
        .order("published_at", { ascending: false });
      if (category) q = q.eq("category", category);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as MarketingArticleSummary[];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useFeaturedArticle() {
  return useQuery<MarketingArticleSummary | null>({
    queryKey: ["marketing-article-featured"],
    queryFn: async () => {
      const { data } = await tenantDb
        .from("marketing_articles")
        .select(SUMMARY_COLS)
        .eq("is_published", true)
        .eq("is_featured", true)
        .order("sort_order", { ascending: true })
        .limit(1)
        .maybeSingle();
      return (data ?? null) as MarketingArticleSummary | null;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useMarketingArticleBySlug(slug: string | undefined) {
  return useQuery<MarketingArticle | null>({
    queryKey: ["marketing-article", slug],
    queryFn: async () => {
      if (!slug) return null;
      const { data } = await tenantDb
        .from("marketing_articles")
        .select("*")
        .eq("slug", slug)
        .eq("is_published", true)
        .maybeSingle();
      return (data ?? null) as MarketingArticle | null;
    },
    enabled: !!slug,
    staleTime: 5 * 60 * 1000,
  });
}

export function useRelatedArticles(slug: string | undefined, related: string[], category: string | undefined) {
  return useQuery<MarketingArticleSummary[]>({
    queryKey: ["marketing-articles-related", slug, related.join(","), category],
    queryFn: async () => {
      if (!slug) return [];
      if (related.length > 0) {
        const { data } = await tenantDb
          .from("marketing_articles")
          .select(SUMMARY_COLS)
          .in("slug", related)
          .eq("is_published", true)
          .limit(3);
        const rows = (data ?? []) as MarketingArticleSummary[];
        if (rows.length >= 3) return rows;
      }
      if (!category) return [];
      const { data } = await tenantDb
        .from("marketing_articles")
        .select(SUMMARY_COLS)
        .eq("category", category)
        .eq("is_published", true)
        .neq("slug", slug)
        .order("published_at", { ascending: false })
        .limit(3);
      return (data ?? []) as MarketingArticleSummary[];
    },
    enabled: !!slug,
    staleTime: 5 * 60 * 1000,
  });
}
